import { useState, type FC } from "react";
import { App, Table } from "antd";
import type { QATableType } from "./QATableType";
import EditFinalCell from "./editCell";
import { useTabsContext } from "../../../../contexts/tabsContext";
import "./qaTable.css";
import { useCommonPropertiesContext } from "../../../../contexts/commonPropertiesContext";
import ActionButton from "../../../masterComponents/actionButton";
import Icon from "../../../masterComponents/Icon";
import { ClearOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { PHRASES_DICTIONARY } from "../../../../entities/phrasesDictionary";
import { useSingleValueActionsById } from "../../../../hooks/useEndpoints/useSingleValueActionsById";

const DefaultCell = ({ value, title = null, onClick = null }) => (
  <div title={title ? title : ""} onClick={onClick}>
    {value}
  </div>
);

const QATable: FC<QATableType> = ({
  metadataInfo,
  tableData,
  fetchItemById,
}) => {
  const { message: messageApi, modal } = App.useApp();

  const [editingKey, setEditingKey] = useState<string | null>(null);

  const { id } = useTabsContext();
  const { currentBatch, choosedBatchToQA } = useCommonPropertiesContext();
  const itemBatch = choosedBatchToQA ?? currentBatch;

  const [validatingAttrCode, setValidatingAttrCode] = useState<string | null>(
    null,
  );
  const [clearingValidationAttrCode, setClearingValidationAttrCode] = useState<
    string | null
  >(null);

  const {
    submitValue,
    deleteFinalValue,
    validateFinalValue,
    clearValidation,
    isLoadingValidateFinalValue,
    isLoadingClearValidation,
  } = useSingleValueActionsById({
    queryParams: { batch: itemBatch, _id: id },
    onSuccess: ({ action }) => {
      fetchItemById({ batch: itemBatch, _id: id });

      if (action === "validateFinalValue") {
        setValidatingAttrCode(null);
      }
      if (action === "clearValidation") {
        setClearingValidationAttrCode(null);
      }
    },
    onError: ({ action, error }) => {
      messageApi.error(error.message);

      if (action === "validateFinalValue") {
        setValidatingAttrCode(null);
      }
      if (action === "clearValidation") {
        setClearingValidationAttrCode(null);
      }
    },
  });

  const handleSubmitFinalValue = (attr, finalValue, displayValue) => {
    submitValue({
      attribute_name: attr,
      final_value: finalValue,
      source_value: displayValue,
    });
  };

  const getCellClassName = (
    record,
    options?: { includeExtracted?: boolean },
  ) => {
    const attrCode = record.attribute?.attr_code;
    const hasFinal = metadataInfo?.finals?.[attrCode];
    const hasValidation = metadataInfo?.validations?.[attrCode];
    const hasExtracted = metadataInfo?.extractions?.[attrCode];

    let className = "";

    if (
      options?.includeExtracted &&
      attrCode &&
      hasExtracted &&
      !hasFinal &&
      !hasValidation
    ) {
      className = "extracted-value";
    } else if (attrCode && hasFinal && !hasValidation) {
      className = "verified-value";
    } else if (attrCode && hasFinal && hasValidation) {
      className = "validated-value";
    }
    return { className };
  };

  const columns = [
    {
      title: "Attribute",
      dataIndex: "attribute",
      key: "attribute",
      className: "attribute",
      width: 1.4,
      render: (text, record) => (
        <span
          style={{
            fontSize: record.isMandatory ? "16px" : "14px",
            fontWeight: record.isMandatory ? "bold" : "normal",
          }}
        >
          {text.attr_code} ({text.attr_descr})
        </span>
      ),
    },
    {
      title: "LLM Based Value",
      dataIndex: "llmExtractionValues",
      key: "llmExtractionValues",
      width: 1,
      render: (_, record) => {
        return <DefaultCell value={record.llmExtractionValues || "-"} />;
      },
      onCell: (record) =>
        record.attribute !== "-" &&
        getCellClassName(record, { includeExtracted: true }),
    },
    {
      title: "Final Value",
      dataIndex: "finalValue",
      key: "finalValue",
      width: 1,
      onCell: (record) => getCellClassName(record),
      render: (_: string, record) => {
        const isEditing = editingKey === record.key.toString();
        const attrCode = record.attribute?.attr_code;
        const hasFinal = metadataInfo?.finals?.[attrCode];
        const hasExtractions = metadataInfo?.extractions?.[attrCode];
        const hasValidation = metadataInfo?.validations?.[attrCode];
        const displayValue =
          attrCode && !hasFinal && hasExtractions
            ? record.llmExtractionValues
            : record.finalValue;

        return isEditing ? (
          <EditFinalCell
            value={displayValue}
            handleSubmit={(newFinalValue) => {
              if (attrCode) {
                handleSubmitFinalValue(attrCode, newFinalValue, displayValue);
                setEditingKey(null);
              }
            }}
            handleClear={() => {
              if (attrCode && !hasValidation) {
                deleteFinalValue({
                  source_value: record.finalValue,
                  attribute_name: attrCode,
                });
                setEditingKey(null);
              } else if (attrCode && hasValidation) {
                modal.warning({
                  title: "Warning!",
                  content: (
                    <div style={{ whiteSpace: "pre-line" }}>
                      {PHRASES_DICTIONARY.WARNINGS.warning_clear_final_value}
                    </div>
                  ),
                  onOk: () => {
                    deleteFinalValue({
                      source_value: record.finalValue,
                      attribute_name: attrCode,
                    });
                    setEditingKey(null);
                  },
                  okCancel: true,
                  transitionName: "ant-fade",
                });
              }
            }}
            isFinalValues={
              attrCode ? metadataInfo?.finals?.[attrCode] : undefined
            }
            onBlur={() => setEditingKey(null)}
          />
        ) : (
          <DefaultCell
            value={displayValue || "-"}
            onClick={() => setEditingKey(record.key.toString())}
          />
        );
      },
    },
    {
      title: "Validated Values",
      dataIndex: "validatedValues",
      key: "validatedValues",
      width: 1,
      onCell: (record) => getCellClassName(record),
      render: (_, record) => {
        const attrCode = record.attribute?.attr_code;
        const hasFinal = metadataInfo?.finals?.[attrCode];
        const hasValidation = metadataInfo?.validations?.[attrCode];

        const buttonStyles = {
          width: "100%",
          gap: "var(--spacing-xs)",
        };

        if (hasFinal && hasValidation) {
          return (
            <ActionButton
              key="clearValidation"
              text="Clear validation"
              icon={<Icon icon={ClearOutlined} />}
              iconPosition="end"
              className="clear-btn"
              styles={{ ...buttonStyles, background: "#E5484D" }}
              onClick={() => {
                modal.warning({
                  title: "Warning!",
                  content: PHRASES_DICTIONARY.WARNINGS.warning_clear_validation,
                  onOk: () => {
                    setClearingValidationAttrCode(attrCode);
                    clearValidation({
                      attribute_name: attrCode,
                      source_value: record.finalValue,
                    });
                  },
                  okCancel: true,
                  transitionName: "ant-fade",
                });
              }}
              loading={
                clearingValidationAttrCode === attrCode &&
                isLoadingClearValidation
              }
            />
          );
        }
        if (hasFinal) {
          return (
            <ActionButton
              key="validate"
              text="Validate"
              icon={<Icon icon={CheckCircleOutlined} />}
              iconPosition="end"
              className="validate-btn"
              styles={{ ...buttonStyles, background: "#2EAF63" }}
              onClick={() => {
                setValidatingAttrCode(attrCode);
                validateFinalValue({
                  attribute_name: attrCode,
                  source_value: record.finalValue,
                });
              }}
              loading={
                validatingAttrCode === attrCode && isLoadingValidateFinalValue
              }
            />
          );
        }
        return <DefaultCell value="-" />;
      },
    },
  ];

  return (
    <>
      <Table
        className="qa-table primary-table"
        columns={columns}
        dataSource={tableData}
        pagination={false}
        style={{
          marginBottom: "10px",
          marginTop: "10px",
          borderBottom: "solid 1px rgb(0, 0, 0, 0.15)",
          borderRight: "solid 1px rgb(0, 0, 0, 0.15)",
        }}
        tableLayout="fixed"
      />
    </>
  );
};

export default QATable;
